import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {KennethMustDie} from "../../KennethMustDie";
import {gsap} from "gsap";
import {IFeatureInfo, SPECIAL_SYMBOL_NAMES} from "../../server/IKMDData";
import {KMDSymbol} from "../../reels/KMDSymbol";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {KMDSound} from "../../sound/KMDSound";

export class KMDAvalancheResultPresentation implements FlowStatePresentation {
    private _tl: gsap.core.Timeline;

    public start(flowStateController: FlowStateController): void {
        this._tl = gsap.timeline({
            onComplete: () => {
                this.completePresentation(flowStateController);
            }
        });

        KennethMustDie.specialReelView.reel.createFillUpStopSymbolsForSpecialReel();

        this._tl.add(() => {
            flowStateController.setSkipCallback(() => this.skip());
        }, 0.1);

        // #245
        this._tl.add(KennethMustDie.multipliersChain.fillUp());

        // #238
        KennethMustDie.AVALANCHE_SYM_DROP_ROW = [false, false, false];

        let stopTime: number = this._tl.totalDuration(),
            fillUpTl: gsap.core.Timeline,
            anticipationTriggered: boolean = false;
        for (let i: number = 0; i < KennethMustDie.model.gameData.reels.length; i++) {
            KennethMustDie.reelView.reelModels[i].createFillUpStopSymbols();

            fillUpTl = KennethMustDie.reelView.reelModels[i].fillUp();
            fillUpTl.add(this.preventSkipOnLastReel(i, flowStateController), 0);

            this._tl.add(fillUpTl, stopTime);

            const fillUpDuration: number = fillUpTl.totalDuration();
            if (i > 0 && i < (KennethMustDie.REELS_NUM - 1)) {
                this._tl.add(KennethMustDie.specialReelView.reel.getFillUpAnimationIdxWise(i - 1), stopTime + (fillUpDuration > 0 ? fillUpDuration - 0.2 : fillUpDuration));
            }

            // #233
            if (KennethMustDie.model.nearWinReels[i + 1]) {
                this._tl.add(() => {
                    KennethMustDie.tryAnticipation(i);

                    // #485
                    if (KennethMustDie.s1CountReelStop === 2) {
                        // #529
                        KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_FIRE);

                        for (let reelId: number = 1; reelId <= i; reelId++) {
                            if (KennethMustDie.model.s1Positions[reelId] > -1) {
                                KennethMustDie.nearWinAnticipation.playAnticipateFireAnimation(reelId, KennethMustDie.model.s1Positions[reelId] + 1, 0);
                            }
                        }

                        // #485 -> already stopped reels
                        for (let reelId: number = i + 1; reelId <= (KennethMustDie.REELS_NUM - 1); reelId++) {
                            const symIdOfS: number = KennethMustDie.model.s1Positions[reelId];
                            if (symIdOfS > -1) {
                                const yPosSym: number = KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(symIdOfS).positionable.position.y;

                                if (yPosSym === (KennethMustDie.SYMBOL_HEIGHT * symIdOfS)) {
                                    KennethMustDie.nearWinAnticipation.playAnticipateFireAnimation(reelId, symIdOfS + 1, 0);
                                }
                            }
                        }
                    }
                });

                stopTime += KennethMustDie.SCATTER_NEAR_WIN_DELAY;

                if (i + 1 === 4) {
                    stopTime += 2;
                }

                if (KennethMustDie.model.xHPos === 1) {
                    stopTime += 2;
                }

                anticipationTriggered = true;
            } else if (anticipationTriggered) {
                anticipationTriggered = false;

                this._tl.add(() => {
                    let nearWinInRemainingReels: boolean = false;
                    for (let nextReelId: number = (i + 1); nextReelId < KennethMustDie.model.nearWinReels.length; nextReelId++) {
                        if (KennethMustDie.model.nearWinReels[nextReelId]) {
                            nearWinInRemainingReels = true;

                            break;
                        }
                    }

                    if (nearWinInRemainingReels) {
                        KennethMustDie.nearWinAnticipation.stopReelAnticipations(-1);
                    } else {
                        KennethMustDie.stopScattersAnticipation();
                    }
                }, (stopTime + fillUpDuration));
            }

            if (fillUpDuration > 0) {
                stopTime += (fillUpDuration / 2);
            }
        }

        this._tl.add(() => {
            KennethMustDie.specialReelView.reel.avalancheSymbolsRemoved = 0;

            KennethMustDie.stopScattersAnticipation();

            const s2Feature: IFeatureInfo = KennethMustDie.model.s2Feature;
            if (s2Feature.originReelId !== -1) {
                const s2Symbol: KMDSymbol = (KennethMustDie.reelView.reelModels[s2Feature.originReelId].getStopSymbolAt(s2Feature.originSymId - 1).positionable as KMDSymbol);

                s2Symbol.originalName = SPECIAL_SYMBOL_NAMES.S2;
                s2Symbol.updateTexture(SPECIAL_SYMBOL_NAMES.S2);

                KennethMustDie.stickyS2.hideStickyS2();
            }

            // only in middle 3 main reels
            for (let reelId: number = 1; reelId < KennethMustDie.REELS_NUM - 1; reelId++) {
                KennethMustDie.stickyWilds.changeToReelSymbol(reelId, true);
                KennethMustDie.stickyXNudges.changeToReelSymbol(reelId);
            }

            // #326
            KennethMustDie.updateWaysOnReelStopActions(KennethMustDie.model.multipliersForReelStop);
        });

        this._tl.to(this, {
            duration: 0.1
        });

        if (KennethMustDie.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            this._tl.timeScale(2);
        }
    }

    private preventSkipOnLastReel(reelId: number, flowStateController: FlowStateController): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (reelId === 4) {
            tl.add(() => {
                flowStateController.setSkipCallback();
            }); //prevents useless skips when last reel has started its stop anyway.
        }
        return tl;
    }

    private skip(): void {
        this._tl.timeScale(5);
    }

    private completePresentation(flowStateController: FlowStateController): void {
        flowStateController.finish();
        this._tl.kill();
    }
}